import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import useResizeObserver from '@/hooks/use-resize-observer';
import { cn } from '@/lib/utils';

import { EDIT_IMAGE_METHOD, editMethodOptions } from '../constants/toggle-group-options';
import { CollapsableCard } from './collapsable-card';
import { ImagePreview } from './image-preview';
import { ImageUpload } from './image-upload';
import { MagicStyle } from './magic-style/magic-style';
import { ObjectRelationSettings } from './object-relation-settings';
import { StylePrompt } from './style-prompt';
import { StyleSettings } from './style-settings/style-settings';

interface ImageRenderingSidebarProps {
  imageId?: string;
  editMethod: EDIT_IMAGE_METHOD | null;
  settings: StyleSettings;
  canGenerate: boolean;
  estateId?: string;
  showMobileSettings: boolean;
  onChangeEstateId: React.Dispatch<React.SetStateAction<string | undefined>>;
  disableSelectObject: boolean;
  onChangeEditMethod: (method: EDIT_IMAGE_METHOD) => void;
  onUploadImage: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  onCreateImage: () => void;
  onSelectImage: (id: string) => void;
  onReplaceSelected: () => void;
  onChangeSettings: Dispatch<SetStateAction<StyleSettings>>;
  sidebarRef?: (node: Element) => void;
}

export const ImageRenderingSidebar = ({
  imageId,
  editMethod,
  canGenerate,
  estateId,
  showMobileSettings,
  onChangeEstateId,
  disableSelectObject,
  onChangeEditMethod,
  onUploadImage,
  onCreateImage,
  onSelectImage,
  onReplaceSelected,
  sidebarRef,
  ...props
}: ImageRenderingSidebarProps) => {
  const { t } = useTranslation();
  const isDesktopView = useResizeObserver(1024);

  const [replacing, setReplacing] = useState(false);

  const styleSidebarSection = (
    <div ref={sidebarRef} className="flex w-full flex-col gap-[6px] sm:gap-3">
      {!replacing && <ImagePreview imageId={imageId} onReplace={() => setReplacing(true)} />}
      {replacing && (
        <ImageUpload
          title={t('yourRoom')}
          imageId={imageId}
          estateId={estateId}
          replacing={replacing}
          onSelectImage={onSelectImage}
          onUpload={onUploadImage}
          onReplaceSelected={onReplaceSelected}
          onCancelUpload={() => setReplacing(false)}
        />
      )}
      <StyleSettings {...props} />
      {appConfig.stylePrompt && <StylePrompt />}
      <ObjectRelationSettings
        estateId={estateId}
        onChangeEstateId={onChangeEstateId}
        disableSelectObject={disableSelectObject}
      />
    </div>
  );

  const magicSidebarSection = (
    <div ref={sidebarRef} className="flex w-full flex-col gap-[6px] sm:gap-3">
      <MagicStyle />

      <CollapsableCard title={t('change')}></CollapsableCard>

      <CollapsableCard title={t('remove')}></CollapsableCard>
    </div>
  );

  return (
    <>
      <div className={cn('block w-full sm:hidden', imageId ? '' : 'mt-4')}>
        {!imageId ? (
          <ImageUpload
            imageId={imageId}
            estateId={estateId}
            replacing={replacing}
            onSelectImage={onSelectImage}
            onUpload={onUploadImage}
          />
        ) : null}
      </div>
      <div
        className={cn(
          isDesktopView
            ? 'min-w-[300px] max-w-[300px]'
            : 'w-full sm:min-w-[150px] sm:max-w-[150px]',
          showMobileSettings ? 'block' : 'hidden sm:block'
        )}
      >
        <ToggleGroup
          className={cn(
            'mb-2 rounded-sm bg-text-primary p-2 shadow-lg shadow-black/10 sm:mb-6',
            isDesktopView ? 'min-w-[300px]' : 'min-w-[150px]',
            !imageId && 'hidden sm:flex'
          )}
          type="single"
          disabled={!imageId}
          value={editMethod}
          onValueChange={onChangeEditMethod}
        >
          {editMethodOptions.map(({ label, value }, index) => (
            <ToggleGroupItem
              key={`edit-image-method-toggle-item-${index}`}
              className={`h-[24px] flex-1 rounded-sm ${editMethod === value ? '!bg-[#2C414B]' : '!bg-transparent'} ${isDesktopView ? 'px-3' : 'px-1'}`}
              value={value}
            >
              <span
                className={`text-[17px] font-semibold ${editMethod === value ? 'text-primary-light' : 'text-white'}`}
              >
                {t(label)}
              </span>
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        {!imageId ? (
          <ImageUpload
            imageId={imageId}
            estateId={estateId}
            replacing={replacing}
            onSelectImage={onSelectImage}
            onUpload={onUploadImage}
          />
        ) : editMethod === EDIT_IMAGE_METHOD.STYLE ? (
          styleSidebarSection
        ) : (
          magicSidebarSection
        )}
        {!!editMethod && (
          <div className="mt-4 flex w-full justify-center gap-2">
            <Button
              disabled={!canGenerate}
              className="mt-2 h-[34px] w-[120px] py-1"
              onClick={onCreateImage}
            >
              {t('create')}
            </Button>
          </div>
        )}
      </div>
    </>
  );
};
